import { useEffect, useRef } from 'react';

import { bind } from 'bind-event-listener';

import { useIsReducedMotion } from '@atlaskit/motion';

import { useCanHover } from './use-can-hover';

export interface MousePosition {
	/**
	 * Mouse X position relative to container
	 */
	x: number;
	/**
	 * Mouse Y position relative to container
	 */
	y: number;
	/**
	 * Raw clientX from mouse event
	 */
	clientX: number;
	/**
	 * Raw clientY from mouse event
	 */
	clientY: number;
}

export interface ContainerRect {
	top: number;
	left: number;
	right: number;
	bottom: number;
	width: number;
	height: number;
}

export interface MouseTrackingCallbacks {
	/**
	 * Called on each position update (mouse move or scroll).
	 * Receives the mouse position relative to the container and the container rect.
	 */
	onPositionUpdate: (position: MousePosition, containerRect: ContainerRect) => void;

	/**
	 * Called when mouse leaves the tracked container.
	 */
	onMouseLeave?: () => void;
}

export interface UseMouseTrackingOptions {
	/**
	 * Ref to the container element for mouse tracking.
	 * If not provided, the component will use wrapperRef's parent element.
	 */
	containerRef?: React.RefObject<HTMLElement>;

	/**
	 * Ref to an element inside the component (used to find parent if containerRef not provided).
	 */
	wrapperRef: React.RefObject<HTMLElement>;

	/**
	 * Whether tracking is enabled. Defaults to true.
	 * Tracking is automatically disabled when canHover is false or reduced motion is on.
	 */
	enabled?: boolean;

	/**
	 * Callbacks for position updates and mouse leave events.
	 */
	callbacks: MouseTrackingCallbacks;
}

/**
 * Hook for tracking mouse position relative to a container element.
 *
 * Provides requestAnimationFrame-batched position updates on mouse move and scroll events.
 * Automatically handles cleanup and respects reduced motion preferences.
 *
 * @example
 * ```tsx
 * useMouseTracking({
 *   containerRef: heroRef,
 *   wrapperRef: overlayRef,
 *   callbacks: {
 *     onPositionUpdate: (position, rect) => {
 *       overlayRef.current?.style.setProperty('--mouse-x', `${position.x}px`);
 *     },
 *     onMouseLeave: () => {
 *       overlayRef.current?.style.setProperty('--mouse-x', '-999px');
 *     },
 *   },
 * });
 * ```
 */
export function useMouseTracking({
	containerRef,
	wrapperRef,
	enabled = true,
	callbacks,
}: UseMouseTrackingOptions): {
	isReducedMotion: boolean;
	canHover: boolean;
} {
	const isReducedMotion = useIsReducedMotion();
	const canHover = useCanHover();

	// Store callbacks in ref to avoid re-binding on callback changes
	const callbacksRef = useRef(callbacks);
	callbacksRef.current = callbacks;

	useEffect(() => {
		if (
			typeof window === 'undefined' ||
			!wrapperRef.current ||
			isReducedMotion ||
			!canHover ||
			!enabled
		) {
			return;
		}

		const container = containerRef?.current || wrapperRef.current.parentElement;
		if (!container) {
			return;
		}

		let rafId: number | null = null;
		let lastMouseX = 0;
		let lastMouseY = 0;

		const updatePosition = () => {
			if (!wrapperRef.current || !container) {
				return;
			}

			// Cancel any pending RAF to batch updates
			if (rafId !== null) {
				cancelAnimationFrame(rafId);
			}

			rafId = requestAnimationFrame(() => {
				rafId = null;
				const containerRect = container.getBoundingClientRect();

				const position: MousePosition = {
					x: lastMouseX - containerRect.left,
					y: lastMouseY - containerRect.top,
					clientX: lastMouseX,
					clientY: lastMouseY,
				};

				const rect: ContainerRect = {
					top: containerRect.top,
					left: containerRect.left,
					right: containerRect.right,
					bottom: containerRect.bottom,
					width: containerRect.width,
					height: containerRect.height,
				};

				callbacksRef.current.onPositionUpdate(position, rect);
			});
		};

		const handleMouseMove = (e: MouseEvent) => {
			lastMouseX = e.clientX;
			lastMouseY = e.clientY;
			updatePosition();
		};

		const handleMouseLeave = () => {
			callbacksRef.current.onMouseLeave?.();
		};

		// Track mouse globally to continue tracking when scrolling
		const unbindMove = bind(window, {
			type: 'mousemove',
			listener: handleMouseMove,
		});

		// Track scroll events to update position as container moves
		const unbindScroll = bind(window, {
			type: 'scroll',
			listener: updatePosition,
			options: { passive: true },
		});

		// Track scrollend for final position update when scrolling stops
		const unbindScrollEnd = bind(window, {
			type: 'scrollend',
			listener: updatePosition,
			options: { passive: true },
		});

		// Track mouse leave on container
		const unbindLeave = bind(container, {
			type: 'mouseleave',
			listener: handleMouseLeave,
		});

		return () => {
			unbindMove();
			unbindScroll();
			unbindScrollEnd();
			unbindLeave();
			if (rafId !== null) {
				cancelAnimationFrame(rafId);
			}
		};
	}, [containerRef, wrapperRef, isReducedMotion, canHover, enabled]);

	return { isReducedMotion, canHover };
}

export interface ProximityThresholds {
	top?: number;
	right?: number;
	bottom?: number;
	left?: number;
}

/**
 * Check if mouse is within a proximity threshold of container edges (including outside)
 * @param clientX - Mouse X position
 * @param clientY - Mouse Y position
 * @param rect - Container bounding rect
 * @param threshold - Default threshold for all edges, or an object with per-edge thresholds
 */
export const isNearContainer = (
	clientX: number,
	clientY: number,
	rect: ContainerRect,
	threshold: number | ProximityThresholds,
): boolean => {
	const thresholds: Required<ProximityThresholds> =
		typeof threshold === 'number'
			? { top: threshold, right: threshold, bottom: threshold, left: threshold }
			: {
					top: threshold.top ?? 0,
					right: threshold.right ?? 0,
					bottom: threshold.bottom ?? 0,
					left: threshold.left ?? 0,
				};

	return (
		clientX >= rect.left - thresholds.left &&
		clientX <= rect.right + thresholds.right &&
		clientY >= rect.top - thresholds.top &&
		clientY <= rect.bottom + thresholds.bottom
	);
};
